/**
 * Video Wall asset helpers — separate from `split-screen.ts` even though
 * the asset shape overlaps because video-wall has different default
 * derivation rules (resolution from `meta.templateConfig` instead of a
 * resolution preset, always `unitType: 'percent'`, no Custom flow).
 */
import type { VideoWallAsset, VideoWallZone } from '../stores/video-wall-dialog-store';
import { SPLIT_SCREEN_PALETTE, getRandomColor } from '../components/SplitScreenCanvas/lib/colors';

const DEFAULT_DOCUMENT_DURATION = 10;

/**
 * Parse a video-wall `templateConfig` string into the overall canvas
 * resolution + per-zone resolutions. Mirrors scio1
 * `videoWallZonesModal.js:91-96` (only the first segment is used for
 * the canvas dimensions; the trailing segments are per-zone metadata
 * we don't currently model).
 *
 * Format examples:
 *   "3840x1080+1920x1080+1920x1080"  → 2-zone (2 Screens, 2x1)
 *   "1920x3240+0+0:3"                 → 3-zone (vertical stack 1x3)
 *
 * Returns `null` when the string is missing / unparseable so the caller
 * can fall back to the asset's existing resolutionWidth/Height.
 */
export function parseTemplateConfig(
  templateConfig: string | undefined | null,
): { width: number; height: number } | null {
  if (!templateConfig) return null;
  const head = templateConfig.split('+')[0];
  if (!head) return null;
  const [w, h] = head.split('x').map((s) => parseInt(s, 10));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { width: w, height: h };
}

/** Shapes a single physical screen can have — landscape 16:9 or portrait 9:16. */
const SCREEN_ASPECTS = [16 / 9, 9 / 16];

/** Screen count: the `:N` suffix, else the number of per-zone `WxH` segments; null when absent. */
function templateScreenCount(templateConfig: string): number | null {
  const suffix = /:(\d+)$/.exec(templateConfig);
  if (suffix) {
    const count = Number(suffix[1]);
    return count > 0 ? count : null;
  }
  const zones = templateConfig
    .split('+')
    .slice(1)
    .filter((segment) => /^\d+x\d+$/.test(segment));
  return zones.length > 0 ? zones.length : null;
}

/** The cols x rows split of `screens` whose per-screen shape is closest to 16:9 or 9:16. */
function templateGrid(
  canvas: { width: number; height: number },
  screens: number,
): { cols: number; rows: number } {
  let best = { cols: screens, rows: 1, distance: Infinity };
  for (let cols = 1; cols <= screens; cols++) {
    if (screens % cols !== 0) continue;
    const rows = screens / cols;
    const aspect = canvas.width / cols / (canvas.height / rows);
    const distance = Math.min(...SCREEN_ASPECTS.map((a) => Math.abs(Math.log(aspect / a))));
    if (distance < best.distance) best = { cols, rows, distance };
  }
  return { cols: best.cols, rows: best.rows };
}

/**
 * End-user label for a video-wall `templateConfig`, in the same `<cols>x<rows> - <W>x<H>` style
 * as the DB-seeded template names in the template picker:
 *   "3840x1080+0+0:2"                → "2x1 - 3840x1080"
 *   "1920x3240+0+0:3"                → "1x3 - 1920x3240"
 *   "3840x1080+1920x1080+1920x1080"  → "2x1 - 3840x1080"
 * Only "<W>x<H>" when the screen count is absent; the input unchanged when it doesn't parse
 * (Edit Screen's layout field accepts free text).
 */
export function formatVideoWallLayout(templateConfig: string): string {
  const raw = templateConfig.trim();
  const canvas = parseTemplateConfig(raw);
  if (!canvas) return raw;
  const size = `${canvas.width}x${canvas.height}`;
  const screens = templateScreenCount(raw);
  if (!screens) return size;
  const { cols, rows } = templateGrid(canvas, screens);
  return `${cols}x${rows} - ${size}`;
}

function fixedPercent(n: number): number {
  return parseFloat((n * 100).toFixed(3));
}

/**
 * Initialise a VideoWallAsset for editing. Mirrors scio1
 * `videoWallZonesModal` constructor (lines 87-165) but extracts the
 * resolution from `meta.templateConfig` (the video-wall path) instead
 * of from a resolution preset (the split-screen path).
 *
 * Always sets `unitType: 'percent'` because video-wall has no pixel
 * mode — the editor doesn't expose unit-type controls.
 */
export function initVideoWallForEdit(input: VideoWallAsset): VideoWallAsset {
  const orientation = input.orientation ?? 'landscape';

  // Derive resolution from templateConfig first; fall back to existing
  // values on the asset (edit-mode reload). Final fallback 1920×1080.
  const fromTemplate = parseTemplateConfig(input.meta?.templateConfig);
  const resolutionWidth =
    typeof input.resolutionWidth === 'number'
      ? input.resolutionWidth
      : (fromTemplate?.width ?? 1920);
  const resolutionHeight =
    typeof input.resolutionHeight === 'number'
      ? input.resolutionHeight
      : (fromTemplate?.height ?? 1080);

  const zones: VideoWallZone[] = (input.screenZones ?? []).map((zone, idx) => {
    const top = typeof zone.top === 'number' ? zone.top : 0;
    const left = typeof zone.left === 'number' ? zone.left : 0;
    const width = typeof zone.width === 'number' ? zone.width : 1;
    const height = typeof zone.height === 'number' ? zone.height : 1;
    const clampedHeight = top + height > 1 ? 1 - top : height;
    const clampedWidth = left + width > 1 ? 1 - left : width;

    // Color allocation — mirror split-screen rules. Templates seeded
    // in MongoDB sometimes carry a numeric color index instead of a
    // hex string; when missing or non-hex, assign a fresh palette
    // color in zone order.
    const isHexColor = typeof zone.color === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(zone.color);
    const color = isHexColor
      ? (zone.color as string)
      : (SPLIT_SCREEN_PALETTE[idx] ?? getRandomColor());

    const id =
      typeof zone.id === 'string' && zone.id.length > 0
        ? zone.id
        : `zone-${idx}-${Math.random().toString(36).slice(2, 11)}`;

    return {
      ...zone,
      id,
      color,
      top: fixedPercent(top),
      left: fixedPercent(left),
      width: fixedPercent(clampedWidth),
      height: fixedPercent(clampedHeight),
      topPixel: zone.topPixel ?? Math.round(top * resolutionHeight),
      leftPixel: zone.leftPixel ?? Math.round(left * resolutionWidth),
      widthPixel: zone.widthPixel ?? Math.round(clampedWidth * resolutionWidth),
      heightPixel: zone.heightPixel ?? Math.round(clampedHeight * resolutionHeight),
      documentDuration: zone.documentDuration ?? DEFAULT_DOCUMENT_DURATION,
    };
  });

  return {
    ...input,
    type: 'splitScreen',
    commonType: 'videoWall',
    orientation,
    resolutionWidth,
    resolutionHeight,
    unitType: 'percent',
    screenZones: zones,
    meta: {
      ...input.meta,
      // Preserve the template's ORIGINAL zone count if it's already
      // recorded — overwriting with `zones.length` would lose the
      // template count when a saved wall has merged zones (current
      // count < original). Fall back to `zones.length` only on first
      // init when meta.zoneNumber hasn't been set yet; template apply
      // explicitly resets this in handleApplyTemplate.
      zoneNumber: input.meta?.zoneNumber ?? zones.length,
    },
    backGroundMusicZone:
      input.backGroundMusicZone == null || Number.isNaN(Number(input.backGroundMusicZone))
        ? null
        : input.backGroundMusicZone,
    audioZone:
      input.audioZone == null || Number.isNaN(Number(input.audioZone)) ? null : input.audioZone,
    primaryZone: input.primaryZone || null,
  };
}
